import i18n from "i18next"
import { reactI18nextModule } from "react-i18next"
import moment from "moment"
import "moment/locale/fr"
import "moment/locale/de"
import "moment/locale/nl"
import "moment/locale/es"
import { SUPPORTED_WEB_LANGUAGES, normalizeWebLanguage, detectBrowserWebLanguage } from "./webLanguages"
import { setWelfareVariantState } from "./welfareVariantState"

const SUPPORTED_LANGUAGES = SUPPORTED_WEB_LANGUAGES
const FALLBACK_LANGUAGE = "en"

// ─────────────────────────────────────────────────────────────────────────────
// Dictionnaires chargés à la demande (décision du 2026-09-05).
// Les trois JSON pesaient ~920 Ko gz dans main.js (plus de la moitié du chunk
// principal) alors qu'une seule langue est active. Chaque langue est un chunk
// séparé ; l'application n'est rendue qu'une fois la langue initiale chargée
// (cf. src/index.js) et tout `changeLanguage` charge d'abord son dictionnaire.
// La langue de repli (EN) est chargée en arrière-plan après le premier rendu.
// Sous Jest, les dictionnaires restent chargés de façon synchrone.
// ─────────────────────────────────────────────────────────────────────────────
const LOCALE_LOADERS = {
  en: () => import(/* webpackChunkName: "locale-en" */ "./locales/en/translations.json"),
  fr: () => import(/* webpackChunkName: "locale-fr" */ "./locales/fr/translations.json"),
  de: () => import(/* webpackChunkName: "locale-de" */ "./locales/de/translations.json"),
  nl: () => import(/* webpackChunkName: "locale-nl" */ "./locales/nl/translations.json"),
  es: () => import(/* webpackChunkName: "locale-es" */ "./locales/es/translations.json"),
}

export const normalizeLanguage = normalizeWebLanguage

/**
 * Même priorité que Base.restoreUserLanguage (V1) et le header V2 :
 * préférence web locale, puis celle du snapshot utilisateur, puis la langue
 * principale du profil (repli EN si non supportée sur le web).
 */
export const resolveInitialLanguage = (
  storage = typeof window !== "undefined" ? window.localStorage : null,
  browser = typeof navigator !== "undefined" ? navigator : null,
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
) => {
  // Public authentication follows the browser, before any cached profile is read.
  if (/^\/(login|activate|register)(\/|$)/i.test(pathname)) return detectBrowserWebLanguage(browser)
  try {
    const webPref = storage?.getItem?.("webLanguagePreference")
    if (webPref && SUPPORTED_LANGUAGES.includes(webPref)) return webPref
    const userData = JSON.parse(storage?.getItem?.("userData") || "null")
    if (userData?.webLanguagePreference && SUPPORTED_LANGUAGES.includes(userData.webLanguagePreference)) {
      return userData.webLanguagePreference
    }
    if (userData?.userMainLanguage) return normalizeLanguage(userData.userMainLanguage)
  } catch (_) {
    // Storage unavailable: use the browser preferences.
  }
  return detectBrowserWebLanguage(browser)
}

const pendingLocales = new Map()

export function loadLocale(lng) {
  const code = normalizeLanguage(lng)
  if (i18n.hasResourceBundle(code, "translations")) return Promise.resolve(code)
  if (!pendingLocales.has(code)) {
    pendingLocales.set(
      code,
      LOCALE_LOADERS[code]()
        .then((module) => {
          i18n.addResourceBundle(code, "translations", module.default || module, true, true)
          return code
        })
        .catch((error) => {
          pendingLocales.delete(code)
          throw error
        })
    )
  }
  return pendingLocales.get(code)
}

const testResources = process.env.NODE_ENV === "test"
  ? {
      en: { translations: require("./locales/en/translations.json") },
      fr: { translations: require("./locales/fr/translations.json") },
      de: { translations: require("./locales/de/translations.json") },
      nl: { translations: require("./locales/nl/translations.json") },
      es: { translations: require("./locales/es/translations.json") },
    }
  : {}

// Synchroniser la locale moment avec la langue i18n
function syncMomentLocale(lng) {
  const momentLocale = normalizeLanguage(lng)
  moment.locale(momentLocale)
  if (typeof document !== "undefined") document.documentElement.lang = momentLocale
}

const initialLanguage = resolveInitialLanguage()

i18n
  .use(reactI18nextModule)
  .init({
    lng: initialLanguage,
    // Repli anglais pour les langues non supportées et les clés manquantes.
    fallbackLng: FALLBACK_LANGUAGE,
    resources: testResources,
    partialBundledLanguages: true,
    // have a common namespace used around the full app
    ns: ["translations"],
    defaultNS: "translations",
    debug: false,
    returnObjects: false,
    returnEmptyString: false,
    interpolation: {
      escapeValue: false,
    },
  })

// Tout changement de langue charge d'abord son dictionnaire (V1 Base/Header,
// login, header V2 : tous passent par i18n.changeLanguage).
const originalChangeLanguage = i18n.changeLanguage.bind(i18n)
i18n.changeLanguage = (lng, callback) =>
  loadLocale(lng)
    .catch(() => null)
    .then(() => originalChangeLanguage(lng, callback))

/** Résolue quand la langue initiale est disponible : src/index.js attend avant le premier rendu. */
export const i18nReady = process.env.NODE_ENV === "test"
  ? Promise.resolve(initialLanguage)
  : loadLocale(initialLanguage)
      .catch(() => null)
      .then(() => {
        if (initialLanguage !== FALLBACK_LANGUAGE) {
          // Repli EN pour les clés manquantes, sans bloquer le premier rendu.
          loadLocale(FALLBACK_LANGUAGE).then(() => i18n.emit("loaded")).catch(() => {})
        }
        return initialLanguage
      })

// Initialiser moment avec la langue courante
syncMomentLocale(i18n.language)

// Mettre à jour moment quand l'utilisateur change de langue
i18n.on("languageChanged", syncMomentLocale)

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulaire « établissement » (issue #1096) : un parc de catégorie
// Laboratoire (zoo.zooType = "lab") lit Établissement, Unité, Hébergement,
// Animalier, Animaux au lieu de Parc, Secteur, Enclos, Soigneur, Collection.
//
// `src/locales/<lng>/lab.json` ne contient que les clés remplacées, avec les
// noms de clés du dictionnaire principal. Chargé seulement pour un labo, il est
// fusionné sous forme de clés de contexte i18next (`cle_lab`,
// `cle_lab_plural`) : quand la variante est active, chaque t(cle) essaie
// d'abord `cle_lab` puis retombe sur `cle`. Un zoo ou un projet ne charge rien.
//
// Les cinq fichiers ont exactement les mêmes clés (labLocales.test.js) :
// i18next cherche `cle_lab` dans la langue de repli avant `cle` dans la langue
// active, une clé absente d'une langue afficherait donc la version anglaise.
// ─────────────────────────────────────────────────────────────────────────────
export const LAB_VARIANT = "lab"

const LAB_LOADERS = {
  en: () => import(/* webpackChunkName: "locale-en-lab" */ "./locales/en/lab.json"),
  fr: () => import(/* webpackChunkName: "locale-fr-lab" */ "./locales/fr/lab.json"),
  de: () => import(/* webpackChunkName: "locale-de-lab" */ "./locales/de/lab.json"),
  nl: () => import(/* webpackChunkName: "locale-nl-lab" */ "./locales/nl/lab.json"),
  es: () => import(/* webpackChunkName: "locale-es-lab" */ "./locales/es/lab.json"),
}

const PLURAL_SUFFIX = /^(.*)_plural$/

/** `{ a: { b: "x", c_plural: "y" } }` → `{ a: { b_lab: "x", c_lab_plural: "y" } }` */
export const toVariantBundle = (overlay, variant = LAB_VARIANT) => {
  if (!overlay || typeof overlay !== "object" || Array.isArray(overlay)) return overlay
  return Object.entries(overlay).reduce((bundle, [key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      bundle[key] = toVariantBundle(value, variant)
      return bundle
    }
    const plural = key.match(PLURAL_SUFFIX)
    bundle[plural ? `${plural[1]}_${variant}_plural` : `${key}_${variant}`] = value
    return bundle
  }, {})
}

const loadedLabOverlays = new Set()
const pendingLabOverlays = new Map()

const testLabResources = process.env.NODE_ENV === "test"
  ? {
      en: require("./locales/en/lab.json"),
      fr: require("./locales/fr/lab.json"),
      de: require("./locales/de/lab.json"),
      nl: require("./locales/nl/lab.json"),
      es: require("./locales/es/lab.json"),
    }
  : null

const readLabOverlay = (code) => testLabResources
  ? Promise.resolve(testLabResources[code])
  : LAB_LOADERS[code]()

/** Charge la surcharge lab d'une langue, après son dictionnaire principal. */
export function loadLabOverlay(lng) {
  const code = normalizeLanguage(lng)
  if (loadedLabOverlays.has(code)) return Promise.resolve(code)
  if (!pendingLabOverlays.has(code)) {
    pendingLabOverlays.set(
      code,
      loadLocale(code)
        .then(() => readLabOverlay(code))
        .then((module) => {
          i18n.addResourceBundle(code, "translations", toVariantBundle(module.default || module), true, true)
          loadedLabOverlays.add(code)
          return code
        })
        .finally(() => pendingLabOverlays.delete(code))
    )
  }
  return pendingLabOverlays.get(code)
}

/** Catégorie Firestore (`{ value }` ou chaîne) → variante de vocabulaire. Seul « lab » bascule. */
export const welfareVariantForZooType = (zooType) => {
  const value = zooType && typeof zooType === "object" ? zooType.value : zooType
  return value === "lab" ? LAB_VARIANT : null
}

let _welfareVariant = null

const _originalT = i18n.t.bind(i18n)
i18n.t = function (key, options, ...rest) {
  if (_welfareVariant && (!options || !options.context)) {
    options = { ...(options || {}), context: _welfareVariant }
  }
  return _originalT(key, options, ...rest)
}

// En mode labo, un changement de langue charge aussi la surcharge de la langue cible.
const changeLanguageWithoutVariant = i18n.changeLanguage
i18n.changeLanguage = (lng, callback) =>
  (_welfareVariant ? loadLabOverlay(lng).catch(() => null) : Promise.resolve())
    .then(() => changeLanguageWithoutVariant(lng, callback))

const refreshTranslations = () => i18n.emit("languageChanged", i18n.language)

export function setWelfareVariant(variant) {
  const next = variant || null
  if (next === _welfareVariant) return Promise.resolve(next)
  _welfareVariant = next
  setWelfareVariantState(next)
  // Re-render every component using withNamespaces / useTranslation, once the
  // overlay of the active language is available.
  if (!next) {
    refreshTranslations()
    return Promise.resolve(next)
  }
  return loadLabOverlay(i18n.language || initialLanguage)
    .catch(() => null)
    .then(() => {
      if (_welfareVariant === next) refreshTranslations()
      return next
    })
}

export function getWelfareVariant() {
  return _welfareVariant
}

export default i18n
